import Immutable from 'immutable'
import ActionConstants from 'constants/ActionConstants'
import ConversationConstants from 'constants/ConversationConstants'
import EndUserConstants from 'constants/EndUserConstants'

const EndUserStore = Marty.createStore({
  id: 'EndUserStore',
  LAST_ES_FETCH_ID: '',

  getInitialState() {
    return {
      endUsersById: Immutable.fromJS({}),
      searchResults: {},
      endUserUpdateStateById: Immutable.Map(),
      endUserUnsubscribeStateById: Immutable.Map(),
      elasticSearchResults: {},
      endUserTimeLineById: {},
      endUserSegmentsById: {}
    }
  },

  rehydrate(dehydratedState) {
    dehydratedState.endUserUpdateStateById = Immutable.fromJS(dehydratedState.endUserUpdateStateById)
    dehydratedState.endUsersById = Immutable.fromJS(dehydratedState.endUsersById)
    return this.state = dehydratedState
  },

  handlers: {
    refreshEndUsers: ActionConstants.REFRESH_ALL_PLZ,
    fetchEndUserDone: EndUserConstants.END_USER_FETCH_DONE,
    fetchEndUserListDone: EndUserConstants.END_USER_LIST_FETCH_DONE,

    fetchEndUsersDone: EndUserConstants.END_USERS_FETCH_DONE,
    fetchEndUserSegmentsDone: EndUserConstants.END_USER_SEGMENTS_FETCH_DONE,

    createEndUserDone: EndUserConstants.CREATE_USER_FOR_ORG_ID_DONE,
    setEndUserFromPusher: EndUserConstants.SET_END_USER_FROM_PUSHER,
    updatingEndUser: EndUserConstants.END_USER_UPDATE_STARTING,
    updateEndUserDone: EndUserConstants.END_USER_UPDATE_DONE,
    unsubscribeEndUserStarting: EndUserConstants.END_USER_UNSUBSCRIBE_STARTING,
    unsubscribeEndUserDone: EndUserConstants.END_USER_UNSUBSCRIBE_DONE,

    searchEndUserDone: EndUserConstants.SEARCH_END_USER_DONE,
    searchConversationsDone: ConversationConstants.SEARCH_ALL_CONVERSATIONS_DONE,
    elasticSearchEndUserDone: EndUserConstants.ELASTIC_SEARCH_END_USER_DONE,

    fetchEndUserAttributesDone: EndUserConstants.END_USER_ATTRIBUTES_FETCH_DONE,

    deleteEndUsersDone: [EndUserConstants.DELETE_END_USERS_DONE, EndUserConstants.DELETE_END_USERS_BULK_DONE]
  },

  refreshEndUsers() {
    // This should already be fetched, no good way to do this right now
    const orgId = this.app.stores.OrgStore.getCurrentOrg().id
    return this.app.queries.EndUserQueries.fetchRecentEndUsers(orgId)
  },

  getEndUserSegments(endUserId) {
    if (endUserId > 0) {
      return this.state.endUserSegmentsById[endUserId]
    } else {
      return false
    }
  },

  fetchEndUserSegmentsDone(endUserId, endUserSegments) {
    return this.setState({
      endUserSegmentsById: Object.assign({}, this.state.endUserSegmentsById, { [endUserId]: endUserSegments })
    })
  },

  searchEndUserDone(orgId, query, searchResult) {
    const searchResults = _.cloneDeep(this.state.searchResults)
    searchResults[query] = searchResult
    this.setState({ searchResults })
    return this.fetchEndUsers(_.map(searchResult, item => item._source.id))
  },

  // We lazily look up end users here in response to conversation searches
  // because we need the state from this store to de-dupe lookups.
  searchConversationsDone(query, searchResult) {
    return this.fetchEndUsers(_.map(searchResult, item => item._source.endUserId))
  },

  getSearchQuery(searchQuery) {
    const query = _.cloneDeep(searchQuery || {})
    return query
  },

  fetchLastElasticSearchResults() {
    return this.state.elasticSearchResults[this.LAST_ES_FETCH_ID]
  },

  fetchElasticSearchResult(query, from, size, sortField, sortDirection) {
    const transFormedQuery = this.getSearchQuery(query)
    const fetchId = this.generateElasticSearchCacheId(transFormedQuery, from, size, sortField, sortDirection)
    return this.fetch({
      id: `fetchElasticSearchResult${ fetchId }`,
      dependsOn: this.app.stores.OrgStore.getOrFetchCurrentOrg(),
      locally() {
        return this.state.elasticSearchResults[fetchId]
      },
      remotely() {
        const orgId = this.app.stores.OrgStore.getCurrentOrg().id
        return this.app.queries.EndUserQueries.elasticSearchEndUser(orgId, transFormedQuery, from, size, sortField, sortDirection)
      }
    })
  },

  generateElasticSearchCacheId(query, from, size, sortField, sortDirection) {
    const sizeQuery = {}
    if (from != null) { sizeQuery.from = from }
    if (size != null) { sizeQuery.size = size }
    const sortQuery = {}
    if (sortField != null) { sortQuery.sortField = sortField }
    if (sortDirection != null) { sortQuery.sortDirection = sortDirection }
    return `${ JSON.stringify(query) }${ JSON.stringify(sizeQuery) }${ JSON.stringify(sortQuery) }`
  },

  elasticSearchEndUserDone(orgId, query, from, size, sortField, sortDirection, searchResult) {
    const fetchId = this.generateElasticSearchCacheId(query, from, size, sortField, sortDirection)
    this.LAST_ES_FETCH_ID = fetchId
    const elasticSearchResults = _.assign({}, this.state.elasticSearchResults)
    elasticSearchResults[fetchId] = searchResult
    return this.setState({ elasticSearchResults })
  },

  getSearchResults() {
    return this.state.searchResults
  },

  resetSearchResults() {
    return this.setState({ searchResults: {}})
  },

  resetElasticSearchResults() {
    return this.setState({ elasticSearchResults: {}})
  },

  fetchEndUserListDone(endUsers) {
    let { endUsersById } = this.state
    _.each(endUsers, endUser => endUsersById = endUsersById.set(endUser.id, endUser))
    return this.setState({ endUsersById })
  },

  fetchEndUsersDone(endUsers) {
    return this.fetchEndUserListDone(endUsers)
  },

  getEndUsersById() {
    return this.state.endUsersById.toJS()
  },

  getEndUser(endUserId) {
    if (endUserId > 0) {
      return this.state.endUsersById.get(endUserId)
    } else {
      return false
    }
  },

  getOrFetchEndUser(endUserId) {
    return this.fetch({
      id: `fetchEndUser${ endUserId }`,
      locally() {
        return this.getEndUser(endUserId)
      },
      remotely() {
        return this.app.queries.EndUserQueries.fetchEndUser(endUserId)
      }
    })
  },

  fetchEndUserDone(endUserID, endUser) {
    return this.setEndUser(endUser)
  },

  fetchEndUserForConversation(conversationId) {
    return this.fetch({
      id: `fetchEndUserForConversation${ conversationId }`,
      dependsOn: this.app.stores.ConversationStore.getOrFetchConversation(conversationId),
      locally() {
        const conversation = this.app.stores.ConversationStore.getConversationById(conversationId)
        return this.getEndUser(conversation.endUserId)
      },
      remotely() {
        const conversation = this.app.stores.ConversationStore.getConversationById(conversationId)
        return this.app.queries.EndUserQueries.fetchEndUser(conversation.endUserId)
      }
    })
  },

  unfetchedEndUserIds(endUserIds) {
    return _.filter(endUserIds, endUserId => {
      return (this.state.endUsersById.get(endUserId) == null)
    })
  },

  fetchEndUsers(endUserIds) {
    const id = `fetchEndUserForConversation${ endUserIds }`
    const unfetchedEndUserIds = this.unfetchedEndUserIds(endUserIds)
    return this.fetch({
      id,
      locally() {
        if (_.isEmpty(unfetchedEndUserIds) || this.hasAlreadyFetched(id)) {
          return _.pick(this.state.endUsersById.toJS(), endUserIds)
        }
      },
      remotely() {
        return this.app.queries.EndUserQueries.fetchEndUsers(unfetchedEndUserIds)
      }
    })
  },

  fetchEndUserAttributes() {
    return this.fetch({
      id: 'fetchEndUserAttributes',
      dependsOn: this.app.stores.OrgStore.getOrFetchCurrentOrg(),
      locally() {
        if (this.hasAlreadyFetched('fetchEndUserAttributes')) {
          return this.state.attributes
        }
      },
      remotely() {
        const orgId = this.app.stores.OrgStore.getCurrentOrg().id
        return this.app.queries.EndUserQueries.fetchEndUserAttributesForOrg(orgId)
      }
    })
  },

  fetchEndUserAttributesDone(attributes) {
    attributes.properties.push({ name: 'tags', displayName: 'Drift Tags', type: 'TAGS' })
    attributes.events = _.sortBy(attributes.events, attribute => attribute.displayName)
    attributes.properties = _.sortBy(attributes.properties, attribute => attribute.displayName)
    return this.setState({ attributes })
  },

  createEndUserDone(endUser) {
    return this.setEndUser(endUser)
  },

  deleteEndUsersDone(endUserIds) {
    let { endUsersById } = this.state
    _.each(endUserIds, endUserId => endUsersById = endUsersById.delete(endUserId))
    this.setState({ endUsersById })
    // wait for ES cluster to catch up and invalidate the search cache to get new ones
    return setTimeout(this.resetElasticSearchResults.bind(this), 5000)
  },

  setEndUserFromPusher(endUser) {
    if (_.isEmpty(endUser)) { return }
    return this.setEndUser(endUser)
  },

  updateEndUser(endUserId, endUserParams) {
    return this.app.queries.EndUserQueries.updateEndUser(endUserId, endUserParams)
  },

  updateEndUserDone(endUser) {
    const endUserUpdateStateById = this.state.endUserUpdateStateById.set(endUser.id.toString(), EndUserConstants.END_USER_UPDATE_DONE)
    this.setState({ endUserUpdateStateById })
    return this.setEndUser(endUser)
  },

  unsubscribeEndUserStarting(endUserId) {
    const endUserUnsubscribeStateById = this.state.endUserUnsubscribeStateById.set(endUserId, EndUserConstants.END_USER_UNSUBSCRIBE_STARTING)
    return this.setState({ endUserUnsubscribeStateById })
  },

  unsubscribeEndUserDone(endUserId) {
    const endUserUnsubscribeStateById = this.state.endUserUnsubscribeStateById.set(endUserId, EndUserConstants.END_USER_UNSUBSCRIBE_DONE)
    return this.setState({ endUserUnsubscribeStateById })
  },

  updatingEndUser(endUserId) {
    const endUserUpdateStateById = this.state.endUserUpdateStateById.set(endUserId, EndUserConstants.END_USER_UPDATE_STARTING)
    return this.setState({ endUserUpdateStateById })
  },

  getEndUserUpdateStateById(endUserId) {
    return this.state.endUserUpdateStateById.get(endUserId)
  },

  getEndUserUnsubscribeStateById(endUserId) {
    return this.state.endUserUnsubscribeStateById.get(endUserId)
  },

  setEndUser(endUser) {
    const endUsersById = this.state.endUsersById.set(endUser.id, endUser)
    return this.setState({ endUsersById })
  }
})

export default EndUserStore



// WEBPACK FOOTER //
// ./src/scripts/stores/EndUserStore.js